import type { Duration, Locale } from 'date-fns';
import { tz, TZDate } from '@date-fns/tz';
import { differenceInDays, eachDayOfInterval, endOfDay, format, isAfter, isBefore, isEqual, isSameDay, startOfDay, subSeconds } from 'date-fns';
import { de, enGB, fr } from 'date-fns/locale';

const locales: Record<string, Locale> = {
    de,
    fr,
    en: enGB,
};

/**
 * Creates a date object with timezone set to zurich
 * @param input date string to parse
 * @returns The date in the Zurich timezone.
 */
export function zurichDate(...input: (number | string | undefined | null)[]): TZDate {
    // @ts-expect-error haltdschnorre
    return input ? TZDate.tz('Europe/Zurich', ...input) : TZDate.tz('Europe/Zurich');
}

/**
 * Creates a date object with timezone set to zurich, with time always set to 00:00
 * @param input date string to parse
 * @returns The date in the Zurich timezone.
 */
export function zurichDay(...input: (number | string | undefined | null)[]): TZDate {
    return startOfDay(zurichDate(...input));
}

/**
 * Checks if date1 is before or equal to date2.
 * @param date1 The first date.
 * @param date2 The second date.
 * @returns True if date1 is before or equal to date2.
 */
export function isBeforeOrEqual(date1: Date, date2: Date): boolean {
    return isBefore(date1, date2) || isEqual(date1, date2);
}

/**
 * Checks if date1 is after or equal to date2.
 * @param date1 The first date.
 * @param date2 The second date.
 * @returns True if date1 is after or equal to date2.
 */
export function isAfterOrEqual(date1: Date, date2: Date): boolean {
    return isAfter(date1, date2) || isEqual(date1, date2);
}

/**
 * Formats the date as 'yyyy-MM-dd\'T\'HH:mm'.
 * @param date The date to format.
 * @returns The formatted date.
 */
export function formatDateTimeISO(date?: Date): string {
    return formatDate(date, 'yyyy-MM-dd\'T\'HH:mm');
}

/**
 * Formats the date as 'yyyy-MM-dd'.
 * @param date The date to format.
 * @returns The formatted date.
 */
export function formatDateISO(date?: Date): string {
    return formatDate(date, 'yyyy-MM-dd');
}

/**
 * Formats a given date localized to "dd.MM.yyyy" (in DE) (e.g. 01.05.2025).
 *
 * @param date - The date to format.
 * @returns The formatted date.
 */
export function formatDateShort(date?: Date): string {
    const format = useNuxtApp().$i18n.t('core.dateFormat.formatDateShort');
    return formatDate(date, format);
}

/**
 * Formats a given date localized to "dd. MMM" (in DE) (e.g. 01. Jan).
 *
 * @param date - The date to format.
 * @returns The formatted date.
 */
export function formatMonthMedium(date?: Date): string {
    const format = useNuxtApp().$i18n.t('core.dateFormat.formatMonthMedium');
    const formatted = formatDate(date, format);
    return transformJBMMonths(formatted);
}

/**
 * Formats a given date localized to "dd. MMM yyyy" (in DE) (e.g. 01. Jan 2022).
 *
 * @param date - The date to format.
 * @returns The formatted date.
 */
export function formatMonthMediumYear(date?: Date): string {
    const format = useNuxtApp().$i18n.t('core.dateFormat.formatMonthMediumYear');
    const formatted = formatDate(date, format);
    return transformJBMMonths(formatted);
}

/**
 * Formats a given date localized to "dd. MMM yyyy HH:mm" (in DE) (e.g. 01. Jan 2022 20:00).
 *
 * @param date - The date to format.
 * @returns The formatted date.
 */
export function formatMonthMediumYearTime(date?: Date): string {
    const format = useNuxtApp().$i18n.t('core.dateFormat.formatMonthMediumYearTime');
    const formatted = formatDate(date, format);
    return transformJBMMonths(formatted);
}

/**
 * Formats a given date localized to "E dd. MMM yyyy" (in DE) (e.g. Fr. 28. Feb. 2025).
 *
 * @param date - The date to format.
 * @returns The formatted date.
 */
export function formatWeekdayMediumYear(date?: Date): string {
    const format = useNuxtApp().$i18n.t('core.dateFormat.formatWeekdayMediumYear');
    const formatted = formatDate(date, format);
    return transformJBMMonths(formatted);
}

/**
 * Formats a given date localized to "E dd. MMM" (in DE) (e.g. Fr. 28. Feb.).
 *
 * @param date - The date to format.
 * @returns The formatted date.
 */
export function formatWeekdayMedium(date?: Date): string {
    const format = useNuxtApp().$i18n.t('core.dateFormat.formatWeekdayMedium');
    const formatted = formatDate(date, format);
    return transformJBMMonths(formatted);
}

/**
 * Formats a given date localized to "E dd. MMM yyyy HH:mm" (in DE) (e.g. Fr. 28. Feb. 2025 20:00).
 *
 * @param date - The date to format.
 * @returns The formatted date.
 */
export function formatWeekdayMediumYearTime(date?: Date): string {
    const format = useNuxtApp().$i18n.t('core.dateFormat.formatWeekdayMediumYearTime');
    const formatted = formatDate(date, format);
    return transformJBMMonths(formatted);
}

/**
 * Formats the date as 'HH:mm'.
 * @param date The date to format.
 * @returns The formatted date.
 */
export function formatTime(date?: Date): string {
    return formatDate(date, 'HH:mm');
}

/**
 * Formats the date as weekday 'Mo', 'Tu'.
 * @param date The date to format.
 * @returns Weekday
 */
export function formatWeekday(date?: Date): string {
    return formatDate(date, 'EEEEEE');
}

/**
 * Formats the date as 'MMM'.
 * @param date The date to format.
 * @returns The formatted date.
 */
export function formathMonthShort(date?: Date): string {
    const formatted = formatDate(date, 'MMM');
    return transformJBMMonths(formatted);
}

/**
 * Apply JBM Specific month formatting
 * According to DIN Norms, but contradicting Unicode CLDR
 *
 * @param formatted - The formatted date string.
 * @returns The adjusted formatted date string.
 */
export function transformJBMMonths(formatted: string): string {
    return formatted
        .replace('Sep.', 'Sept.');
}

/**
 * Formats the date using the specified format pattern.
 * @param date The date to format.
 * @param f The format pattern.
 * @returns The formatted date.
 */
export function formatDate(date: Date | undefined, f: string): string {
    if (!date) {
        return '';
    }
    try {
        return format(date, f, {
            locale: locales[useNuxtApp().$i18n.locale.value],
            in: tz('Europe/Zurich'),
        });
    }
    catch (e) {
        return '';
    }
}

/**
 *
 * @param totalMinutes The number of minutes to format.
 * @returns Format duration as "2 h 30 min", "3 h 45 min", etc.
 */
export function formatTimeDuration(totalMinutes: number): string {
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;

    let result = '';
    if (hours > 0)
        result += useNuxtApp().$i18n.t('core.duration.hours', { count: hours });
    if (minutes > 0 || result === '')
        result += ` ${useNuxtApp().$i18n.t('core.duration.minutes', { count: minutes })}`;

    return result.trim();
};

/**
 *
 * @param minutes The number of minutes to format.
 * @returns A string like "2 h", "2¼ h", "2½ h", "2¾ h".
 */
export function formatHoursHuman(minutes: number): string {
    const roundedMinutes = Math.round(minutes / 15) * 15; // Round to nearest 15 minutes
    const hours = Math.floor(roundedMinutes / 60);
    const remainingMinutes = roundedMinutes % 60;

    let fraction = '';
    if (remainingMinutes === 15) {
        fraction = '¼';
    }
    else if (remainingMinutes === 30) {
        fraction = '½';
    }
    else if (remainingMinutes === 45) {
        fraction = '¾';
    }

    const count = `${hours}${fraction ? `${fraction}` : ''}`;
    return useNuxtApp().$i18n.t('core.duration.hours', { count });
};

/**
 *
 * @param minutes The number of minutes to format.
 * @returns Format minutes as "2 min", "3 min", "4 min", etc.
 */
export function formatMinutes(minutes: number): string {
    return useNuxtApp().$i18n.t('core.duration.minutes', { count: minutes });
};

/**
 *
 * @param minutes The number of minutes to format.
 * @returns Format minutes as "2’", "3’", "4’", etc.
 */
export function formatMinutesShort(minutes: number): string {
    return useNuxtApp().$i18n.t('core.duration.minutesShort', { count: minutes });
};

/**
 *
 * @param nights The number of nights to format.
 * @returns Format nights as "1 night", "2 nights", "3 nights", etc.
 */
export function formatNights(nights: number): string {
    return useNuxtApp().$i18n.t('core.duration.nights', { count: nights });
};

/**
 *
 * @param days The number of days to format.
 * @returns Format days as "1 day", "2 days", "3 days", etc.
 */
export function formatDays(days: number): string {
    return useNuxtApp().$i18n.t('core.duration.days', { count: days });
};

/**
 *
 * @param dateFrom Date from
 * @param dateUntil Date until
 * @param nights If show nights or days (default: false = days)
 * @returns "3 days" or "3 nights"
 */
export function formatFromToDuration(dateFrom: Date | undefined, dateUntil: Date | undefined, nights = false): string {
    if (!dateFrom || !dateUntil) {
        return '';
    }
    const days = differenceInDays(dateUntil, dateFrom);

    if (days >= 1) {
        return nights ? formatNights(days) : formatDays(days);
    }
    return '';
}

/**
 *
 * @param dateFrom Date from
 * @param dateUntilProp Date until
 * @returns Formatted date range
 */
export function formatDateFromTo(dateFrom: Date | undefined, dateUntilProp: Date | undefined, excludeLastDay = false, showWeekDay = false): string {
    let dateUntil = dateUntilProp;

    if (dateUntilProp && excludeLastDay && dateUntilProp.getHours() === 0 && dateUntilProp.getMinutes() === 0) {
        dateUntil = subSeconds(dateUntilProp, 1);
    }

    if (!dateFrom) {
        return '';
    }

    const formatDateSingleFromFunc = showWeekDay ? formatWeekdayMediumYear : formatMonthMediumYear;
    const formatDateSingleFromTimeFunc = showWeekDay ? formatWeekdayMediumYearTime : formatMonthMediumYearTime;
    const formatDateRangeFromFunc = showWeekDay ? formatWeekdayMedium : formatMonthMedium;
    const formatDateRangeToFunc = showWeekDay ? formatWeekdayMediumYear : formatMonthMediumYear;

    if (!dateUntil || isEqual(dateFrom, dateUntil)) {
        return formatDateSingleFromFunc(dateFrom);
    }
    if (isSameDay(dateFrom, dateUntil)) {
        return isWholeDay(dateFrom, dateUntil)
            ? formatDateSingleFromFunc(dateFrom)
            : `${formatDateSingleFromTimeFunc(dateFrom)} - ${formatTime(dateUntil)}`;
    }
    return `${formatDateRangeFromFunc(dateFrom)} - ${formatDateRangeToFunc(dateUntil)}`;
};

/**
 * Parses ISO duration
 * @param iso8601Duration
 * @returns The parsed duration in years, months, weeks, days, hours, minutes and seconds.
 */
export function parseISODuration(iso8601Duration: string): Duration {
    const iso8601DurationRegex = /(-)?P(?:([.,\d]+)Y)?(?:([.,\d]+)M)?(?:([.,\d]+)W)?(?:([.,\d]+)D)?(?:T(?:([.,\d]+)H)?(?:([.,\d]+)M)?(?:([.,\d]+)S)?)?/;

    const matches = iso8601Duration.match(iso8601DurationRegex);

    return {
        years: matches?.[2] ? Number.parseInt(matches[2]) : 0,
        months: matches?.[3] ? Number.parseInt(matches[3]) : 0,
        weeks: matches?.[4] ? Number.parseInt(matches[4]) : 0,
        days: matches?.[5] ? Number.parseInt(matches[5]) : 0,
        hours: matches?.[6] ? Number.parseInt(matches[6]) : 0,
        minutes: matches?.[7] ? Number.parseInt(matches[7]) : 0,
        seconds: matches?.[8] ? Number.parseInt(matches[8]) : 0,
    };
}

/**
 * Checks if two dates represent a whole day (00:00 to 23:59 on the same day).
 * @param startDate The start date.
 * @param endDate The end date.
 * @returns True if the dates represent a whole day.
 */
export function isWholeDay(startDate: Date, endDate: Date): boolean {
    if (!startDate || !endDate) {
        return false;
    }

    // Check if same day
    const isSameDay
        = startDate.getFullYear() === endDate.getFullYear()
            && startDate.getMonth() === endDate.getMonth()
            && startDate.getDate() === endDate.getDate();

    // Check if starts at 00:00
    const startsAtMidnight
        = startDate.getHours() === 0
            && startDate.getMinutes() === 0;

    // Check if ends at 23:59
    const endsAtEndOfDay
        = endDate.getHours() === 23
            && endDate.getMinutes() === 59;

    return isSameDay && startsAtMidnight && endsAtEndOfDay;
}

/**
 * Returns an array of all dates within the given availability ranges.
 * @param ranges Multiple availability ranges with dateFrom and dateUntil.
 * @returns Array of Date objects for each day in the ranges.
 */
export function getDatesFromRanges(ranges: Availability[]) {
    const dates: Date[] = [];
    ranges?.forEach((range) => {
        dates.push(...eachDayOfInterval({ start: range.dateFrom, end: range.dateUntil }));
    });
    return dates;
}

/**
 * Remove past dates from the list of dates.
 * @param dates The list of dates to filter.
 * @param allowToday If today should be allowed in the list of dates.
 * @returns The filtered list of dates.
 */
export function removePastDates(dates: Date[], allowToday = true) {
    if (!allowToday) {
        dates = dates.filter(date => isAfter(date, endOfDay(zurichDate())));
    }
    else {
        dates = dates.filter(date => isAfterOrEqual(date, startOfDay(zurichDate())));
    }
    return dates;
}
